/**
 * Timestamped sponsor notes in Firestore subcollections (admin-only reads in rules).
 * - sponsorOrganizations/{orgId}/orgNoteLog/{noteId}  — directory / all years
 * - sponsors/{sponsorRowId}/sponsorNoteLog/{noteId}    — this event’s participation row
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  updateDoc,
  doc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { firestore, identityAuth, isIdentityIntegrationEnabled } from '../firebase';

export const ORG_NOTE_SUBCOLLECTION = 'orgNoteLog';
export const SPONSOR_ROW_NOTE_SUBCOLLECTION = 'sponsorNoteLog';

const NOTE_LIMIT = 200;

function oneLineSnippet(text, maxLen) {
  const line = (text || '').replace(/\s+/g, ' ').trim().slice(0, maxLen);
  if (line.length >= maxLen) return `${line.slice(0, Math.max(0, maxLen - 1))}…`;
  return line;
}

/**
 * After `signInWithCustomToken` on Freshmeet, `auth.currentUser` often has uid but no
 * email/displayName (profile stays on Fury Identity for the same uid).
 * @param {import('firebase/auth').User | null} user
 * @returns {import('firebase/auth').User | null}
 */
function userWithResolvedProfile(user) {
  if (!user) return null;
  const dn = (user.displayName || '').trim();
  const em = (user.email || '').trim();
  if (dn || em) return user;
  if (!isIdentityIntegrationEnabled() || !identityAuth) return user;
  const id = identityAuth.currentUser;
  if (!id || id.uid !== user.uid) return user;
  const idDn = (id.displayName || '').trim();
  const idEm = (id.email || '').trim();
  if (!idDn && !idEm) return user;
  return {
    uid: user.uid,
    displayName: idDn || null,
    email: idEm || null,
  };
}

/** @param {import('firebase/auth').User | null} user */
export function snapshotAuthor(user) {
  const u = userWithResolvedProfile(user);
  if (!u) {
    return {
      authorUid: null,
      authorEmail: null,
      authorDisplayName: 'Unknown',
    };
  }
  const email = (u.email || '').trim() || null;
  const displayName = (u.displayName || '').trim();
  return {
    authorUid: u.uid || null,
    authorEmail: email,
    authorDisplayName: displayName || email || 'Unknown',
  };
}

/** @param {import('firebase/auth').User | null} user */
export function snapshotLastEditor(user) {
  const a = snapshotAuthor(user);
  return {
    lastEditorUid: a.authorUid,
    lastEditorEmail: a.authorEmail,
    lastEditorDisplayName: a.authorDisplayName,
  };
}

/** Updates sponsors/{id} with who last touched the row via notes (for table + scan). */
async function syncSponsorRowLastTouchFromNote(sponsorRowId, user, mode, newNoteBody) {
  const t = serverTimestamp();
  if (mode === 'edit') {
    const e = snapshotLastEditor(user);
    await updateDoc(doc(firestore, 'sponsors', sponsorRowId), {
      lastTouchByDisplayName: e.lastEditorDisplayName,
      lastTouchByUid: e.lastEditorUid,
      lastTouchAt: t,
    });
    return;
  }
  const a = snapshotAuthor(user);
  const patch = {
    lastTouchByDisplayName: a.authorDisplayName,
    lastTouchByUid: a.authorUid,
    lastTouchAt: t,
  };
  if (typeof newNoteBody === 'string' && newNoteBody.trim()) {
    patch.lastRowNoteSnippet = oneLineSnippet(newNoteBody, 100);
    patch.lastRowNoteByDisplayName = a.authorDisplayName || 'Unknown';
    patch.lastRowNoteAt = t;
  }
  await updateDoc(doc(firestore, 'sponsors', sponsorRowId), patch);
}

/** After a note edit (or reorder), set parent denorm from newest row note. */
export async function resyncLastRowNoteFromLog(sponsorRowId) {
  const list = await fetchSponsorRowNoteLog(sponsorRowId);
  if (!list.length) {
    await updateDoc(doc(firestore, 'sponsors', sponsorRowId), {
      lastRowNoteSnippet: deleteField(),
      lastRowNoteByDisplayName: deleteField(),
      lastRowNoteAt: deleteField(),
    });
    return;
  }
  const top = list[0];
  await updateDoc(doc(firestore, 'sponsors', sponsorRowId), {
    lastRowNoteSnippet: oneLineSnippet(top.body, 100),
    lastRowNoteByDisplayName: top.authorDisplayName || 'Unknown',
    lastRowNoteAt: top.createdAt || serverTimestamp(),
  });
}

/** True if updatedAt is meaningfully after createdAt. */
export function noteWasEdited(n) {
  const c = n?.createdAt?.toMillis?.() ?? null;
  const u = n?.updatedAt?.toMillis?.() ?? null;
  if (c == null || u == null) return false;
  return u > c + 500;
}

export async function fetchOrgNoteLog(orgId) {
  if (!orgId) return [];
  const q = query(
    collection(firestore, 'sponsorOrganizations', orgId, ORG_NOTE_SUBCOLLECTION),
    orderBy('createdAt', 'desc'),
    limit(NOTE_LIMIT)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** @param {import('firebase/auth').User | null} user */
export async function addOrgNoteLog(orgId, bodyText, user) {
  const body = (bodyText || '').trim();
  if (!body || !orgId) throw new Error('Note text and organization are required.');
  const a = snapshotAuthor(user);
  const t = serverTimestamp();
  await addDoc(collection(firestore, 'sponsorOrganizations', orgId, ORG_NOTE_SUBCOLLECTION), {
    body,
    createdAt: t,
    updatedAt: t,
    authorUid: a.authorUid,
    authorEmail: a.authorEmail,
    authorDisplayName: a.authorDisplayName,
    lastEditorUid: null,
    lastEditorEmail: null,
    lastEditorDisplayName: null,
  });
}

/** @param {import('firebase/auth').User | null} user */
export async function updateOrgNoteLog(orgId, noteId, bodyText, user) {
  const body = (bodyText || '').trim();
  if (!body || !orgId || !noteId) throw new Error('Invalid note update.');
  const e = snapshotLastEditor(user);
  await updateDoc(doc(firestore, 'sponsorOrganizations', orgId, ORG_NOTE_SUBCOLLECTION, noteId), {
    body,
    updatedAt: serverTimestamp(),
    lastEditorUid: e.lastEditorUid,
    lastEditorEmail: e.lastEditorEmail,
    lastEditorDisplayName: e.lastEditorDisplayName,
  });
}

export async function fetchSponsorRowNoteLog(sponsorRowId) {
  if (!sponsorRowId) return [];
  const q = query(
    collection(firestore, 'sponsors', sponsorRowId, SPONSOR_ROW_NOTE_SUBCOLLECTION),
    orderBy('createdAt', 'desc'),
    limit(NOTE_LIMIT)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** @param {import('firebase/auth').User | null} user */
export async function addSponsorRowNoteLog(sponsorRowId, bodyText, user) {
  const body = (bodyText || '').trim();
  if (!body || !sponsorRowId) throw new Error('Note text and sponsor row are required.');
  const a = snapshotAuthor(user);
  const t = serverTimestamp();
  await addDoc(collection(firestore, 'sponsors', sponsorRowId, SPONSOR_ROW_NOTE_SUBCOLLECTION), {
    body,
    createdAt: t,
    updatedAt: t,
    authorUid: a.authorUid,
    authorEmail: a.authorEmail,
    authorDisplayName: a.authorDisplayName,
    lastEditorUid: null,
    lastEditorEmail: null,
    lastEditorDisplayName: null,
  });
  await syncSponsorRowLastTouchFromNote(sponsorRowId, user, 'add', body);
}

/** @param {import('firebase/auth').User | null} user */
export async function updateSponsorRowNoteLog(sponsorRowId, noteId, bodyText, user) {
  const body = (bodyText || '').trim();
  if (!body || !sponsorRowId || !noteId) throw new Error('Invalid note update.');
  const e = snapshotLastEditor(user);
  await updateDoc(doc(firestore, 'sponsors', sponsorRowId, SPONSOR_ROW_NOTE_SUBCOLLECTION, noteId), {
    body,
    updatedAt: serverTimestamp(),
    lastEditorUid: e.lastEditorUid,
    lastEditorEmail: e.lastEditorEmail,
    lastEditorDisplayName: e.lastEditorDisplayName,
  });
  await syncSponsorRowLastTouchFromNote(sponsorRowId, user, 'edit');
  await resyncLastRowNoteFromLog(sponsorRowId);
}
